import { useMemo, useState } from 'react';
import './MaterialBrowser.css';

export default function MaterialBrowser({
  textures,
  onClose,
  externalSearch,
  materialSearchMode,
  onMaterialSelect,
}) {
  const [localSearch, setLocalSearch] = useState('');

  // In linked mode the external search (from the material input) drives the filter.
  // In manual mode the browser's own search bar drives it.
  const searchTerm = materialSearchMode ? (externalSearch || '') : localSearch;

  const materials = useMemo(() => {
    if (!textures) return [];
    const term = searchTerm.replace(/\[.*$/, '').trim().toUpperCase().replace(/ /g, '_');
    return textures
      .filter(t => t.fileName.endsWith('.webp'))
      .map(t => ({
        name: t.fileName.replace('.webp', '').toUpperCase(),
        url: t.url,
        fileName: t.fileName,
      }))
      .filter(m => !term || m.name.includes(term))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [textures, searchTerm]);

  const handleItemClick = (materialName) => {
    if (materialSearchMode) {
      // Linked mode: write the value directly into the focused material field
      onMaterialSelect(materialName);
    } else {
      // Manual mode: fill the search bar so the user can read/copy the name
      setLocalSearch(materialName);
    }
  };

  return (
    <div className="material-browser">
      <div className="material-browser-toolbar">
        <span className="material-browser-title">
          {materialSearchMode ? 'Select Material' : 'Material Browser'}
        </span>

        {materialSearchMode ? (
          <span className="material-browser-hint">
            Filtering: <strong>{externalSearch || '…'}</strong>
            <span className="material-browser-hint-sub"> — click to fill field</span>
          </span>
        ) : (
          <input
            className="material-browser-search"
            type="text"
            placeholder="Search materials…"
            value={localSearch}
            onChange={e => setLocalSearch(e.target.value)}
          />
        )}

        <button className="material-browser-close" onClick={onClose} title="Close">×</button>
      </div>

      <div className="material-browser-grid">
        {materials.map(m => (
          <div
            key={m.fileName}
            className="material-browser-item"
            title={m.name}
            // preventDefault stops the material input from blurring when the user
            // clicks an item, keeping materialSearchMode true for the click handler
            onMouseDown={e => e.preventDefault()}
            onClick={() => handleItemClick(m.name)}
          >
            <img src={m.url} alt={m.name} />
          </div>
        ))}
        {materials.length === 0 && (
          <p className="material-browser-empty">No materials found.</p>
        )}
      </div>
    </div>
  );
}
